import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Briefcase, Users, ShieldCheck, MessageSquare } from 'lucide-react';
export function FounderSection() {
  return <section id="about" className="py-24 bg-gradient-to-br from-brand-mauve/10 to-brand-off-white relative overflow-hidden" data-id="element-178">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-179">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-180">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-181">
            {/* Refined Abstract Founder Card */}
            <div className="aspect-[4/5] w-full max-w-md mx-auto lg:mx-0 bg-gradient-to-br from-brand-deep-purple to-brand-charcoal rounded-sm relative overflow-hidden shadow-2xl" data-id="element-182">
              <div className="absolute inset-0 bg-motif-gradient opacity-10" data-id="element-183"></div>

              {/* Circular Logo Motif Arcs Around Initials */}
              <div className="absolute inset-0 flex items-center justify-center opacity-20" data-id="element-184">
                <svg viewBox="0 0 200 200" className="w-full h-full" data-id="element-185">
                  <defs data-id="element-186">
                    <linearGradient id="founderGrad" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-187">
                      <stop offset="0%" stopColor="var(--colour-teal)" data-id="element-188" />
                      <stop offset="50%" stopColor="var(--colour-magenta)" data-id="element-189" />
                      <stop offset="100%" stopColor="var(--colour-lime)" data-id="element-190" />
                    </linearGradient>
                  </defs>
                  <circle cx="100" cy="100" r="80" stroke="url(#founderGrad)" strokeWidth="3" fill="none" strokeDasharray="10 15" data-id="element-191" />
                  <path d="M 100 20 A 80 80 0 0 1 180 100" stroke="url(#founderGrad)" strokeWidth="6" strokeLinecap="round" fill="none" data-id="element-192" />
                  <path d="M 100 180 A 80 80 0 0 1 20 100" stroke="url(#founderGrad)" strokeWidth="6" strokeLinecap="round" fill="none" data-id="element-193" />
                </svg>
              </div>

              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8 text-center" data-id="element-194">
                <div className="w-32 h-32 rounded-full bg-white/10 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center mb-8 shadow-xl" data-id="element-195">
                  <span className="text-6xl font-serif text-transparent bg-clip-text bg-gradient-to-br from-brand-lime via-brand-teal to-brand-magenta" data-id="element-196">
                    JV
                  </span>
                </div>
                <h3 className="text-4xl font-serif mb-2" data-id="element-197">Janet Vantriet</h3>
                <p className="text-brand-lime text-sm uppercase tracking-widest mb-8 font-semibold" data-id="element-198">
                  Founder & Principal
                </p>

                <div className="w-16 h-1 bg-gradient-to-r from-brand-teal to-brand-magenta mb-8" data-id="element-199"></div>

                <p className="text-[15px] text-white/90 italic font-normal leading-relaxed" data-id="element-200">
                  "Every workplace issue I've seen started as something that
                  could have been resolved earlier — if someone had created the
                  right space for it."
                </p>
              </div>
            </div>

            {/* Background accent block */}
            <div className="absolute -bottom-6 -right-6 w-full h-full border-2 border-brand-plum/20 rounded-sm -z-10 max-w-md mx-auto lg:mx-0" data-id="element-201"></div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-202">
            <h2 className="text-4xl md:text-5xl font-serif text-brand-charcoal mb-6 leading-tight" data-id="element-203">
              Behind every engagement is someone who has sat where you sit.
            </h2>

            <p className="text-lg text-brand-charcoal/80 mb-8 leading-relaxed font-normal" data-id="element-204">
              EquipMe was founded by Janet Vantriet, a Certified Organisational
              Ombuds, Nationally Accredited Mediator, and ICF Certified Coach
              with over 20 years of experience in people and culture
              transformation.
            </p>
            <p className="text-lg text-brand-charcoal/80 mb-10 leading-relaxed font-normal" data-id="element-205">
              Janet has worked across corporate, resources, government and
              community sectors, helping organisations bridge the gap between
              policy and people with independence, discretion and practical
              insight.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-10" data-id="element-206">
              <div className="flex items-start gap-3 p-4 bg-white rounded-sm border-l-4 border-brand-teal shadow-sm" data-id="element-207">
                <ShieldCheck size={24} className="text-brand-teal shrink-0 mt-1" data-id="element-208" />
                <div data-id="element-209">
                  <p className="text-[15px] text-brand-charcoal font-semibold mb-1" data-id="element-210">
                    Certified Organisational Ombuds
                  </p>
                  <p className="text-sm text-brand-charcoal/70" data-id="element-211">
                    International Ombudsman Association
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-white rounded-sm border-l-4 border-brand-magenta shadow-sm" data-id="element-212">
                <Users size={24} className="text-brand-magenta shrink-0 mt-1" data-id="element-213" />
                <div data-id="element-214">
                  <p className="text-[15px] text-brand-charcoal font-semibold mb-1" data-id="element-215">
                    Nationally Accredited Mediator
                  </p>
                  <p className="text-sm text-brand-charcoal/70" data-id="element-216">
                    NMAS Accredited
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-white rounded-sm border-l-4 border-brand-orange shadow-sm" data-id="element-217">
                <Award size={24} className="text-brand-orange shrink-0 mt-1" data-id="element-218" />
                <div data-id="element-219">
                  <p className="text-[15px] text-brand-charcoal font-semibold mb-1" data-id="element-220">
                    ICF Certified Coach
                  </p>
                  <p className="text-sm text-brand-charcoal/70" data-id="element-221">
                    Professional Certified Coach (PCC)
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-white rounded-sm border-l-4 border-brand-plum shadow-sm" data-id="element-222">
                <Briefcase size={24} className="text-brand-plum shrink-0 mt-1" data-id="element-223" />
                <div data-id="element-224">
                  <p className="text-[15px] text-brand-charcoal font-semibold mb-1" data-id="element-225">
                    20+ Years Experience
                  </p>
                  <p className="text-sm text-brand-charcoal/70" data-id="element-226">
                    Senior HR & P&C Leadership
                  </p>
                </div>
              </div>
            </div>

            <a href="#consultation" className="inline-flex items-center gap-3 bg-brand-teal hover:bg-brand-deep-purple text-white px-8 py-4 rounded-sm font-semibold transition-all shadow-lg shadow-brand-teal/30 hover:shadow-xl group" data-id="element-227">
              <MessageSquare size={20} data-id="element-228" />
              "I'd welcome a conversation about what you're facing. — Janet"
            </a>

            <div className="mt-10 pt-10 border-t border-gray-200" data-id="element-229">
              <p className="text-brand-charcoal/80 text-[15px] italic leading-relaxed font-normal" data-id="element-230">
                "Janet helped us navigate a complex cultural issue with
                sensitivity and practical wisdom. Her independence and
                discretion were invaluable."
              </p>
              <p className="text-brand-teal text-sm mt-3 font-semibold uppercase tracking-wider" data-id="element-231">
                — Sarah Mitchell, Chief People Officer
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}